import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import {
  LIST_COLS,
  WorkflowDefinition,
  shortName,
  triggerMeta,
} from "./s09-workflow.models";

export interface S09ListRow {
  def: WorkflowDefinition;
  code: string;
  shortName: string;
  codes: string;
  codeChips: string[];
  trigger: string;
  status: string;
  statusBorder: string;
  statusFg: string;
  version: string;
  owner: string;
  lastRun: string;
  instances: string;
  failure: string;
  failFg: string;
  group: string;
}

@Component({
  selector: "app-s09-workflow-list",
  templateUrl: "./s09-workflow-list.component.html",
  styleUrls: ["./s09-workflow.scss"],
  standalone: false,
})
export class S09WorkflowListComponent implements OnChanges {
  @Input() definitions: WorkflowDefinition[] = [];
  @Input() designMetaById: Record<number, { code?: string; codes?: string[]; group?: string }> = {};
  @Input() loading = false;
  @Output() openWorkflow = new EventEmitter<WorkflowDefinition>();
  @Output() create = new EventEmitter<void>();
  @Output() cloneWorkflow = new EventEmitter<WorkflowDefinition>();
  @Output() deleteWorkflow = new EventEmitter<WorkflowDefinition>();
  @Output() showLogs = new EventEmitter<WorkflowDefinition>();

  cols = LIST_COLS;
  search = "";
  filterTrigger = "";
  rows: S09ListRow[] = [];
  groups: { key: string; label: string; sub: string; rows: S09ListRow[] }[] = [];

  ngOnChanges(_: SimpleChanges): void {
    this.rebuild();
  }

  onSearch(): void {
    this.rebuild();
  }

  setFilter(t: string): void {
    this.filterTrigger = t;
    this.rebuild();
  }

  private rebuild(): void {
    const term = this.search.trim().toLowerCase();
    this.rows = (this.definitions || []).map((d) => this.toRow(d)).filter((r) => {
      if (this.filterTrigger && r.trigger.toLowerCase() !== this.filterTrigger.toLowerCase()) {
        return false;
      }
      if (!term) return true;
      return (
        r.shortName.toLowerCase().includes(term) ||
        r.code.toLowerCase().includes(term) ||
        r.codes.toLowerCase().includes(term) ||
        r.trigger.toLowerCase().includes(term) ||
        (r.def.wf_name || "").toLowerCase().includes(term)
      );
    });

    const order = ["patient", "account", "shared", "other"];
    const labels: Record<string, string> = {
      patient: "Patient situations",
      account: "Account / office",
      shared: "Shared routines",
      other: "Other automations",
    };
    const buckets: Record<string, S09ListRow[]> = {};
    this.rows.forEach((r) => {
      const g = r.group || "other";
      (buckets[g] ||= []).push(r);
    });
    this.groups = order
      .filter((k) => buckets[k]?.length)
      .map((k) => ({
        key: k,
        label: labels[k] || k,
        sub: `${buckets[k].length} workflow${buckets[k].length === 1 ? "" : "s"}`,
        rows: buckets[k],
      }));
  }

  private toRow(def: WorkflowDefinition): S09ListRow {
    const meta = this.designMetaById[def.wf_def_id] || {};
    const code = meta.code || `WF${String(def.wf_def_id).padStart(3, "0")}`;
    const name = shortName(def.wf_name || "", code) || def.wf_name || "Untitled";
    const trig = def.trigger_type || "event";
    const tMeta = triggerMeta(
      trig === "schedule" ? "schedule" : trig === "manual" ? "manual" : "status"
    );
    const st = (def.status || "DRAFT").toUpperCase();
    const statusStyle = this.statusStyle(st);
    const fail = Number(def.failure_rate || 0);
    const codeChips = (meta.codes || []).map((c) => String(c).trim()).filter(Boolean);
    return {
      def,
      code,
      shortName: name,
      codes: codeChips.join(" · ") || "—",
      codeChips,
      trigger: tMeta.label,
      status: st,
      statusBorder: statusStyle.border,
      statusFg: statusStyle.fg,
      version: def.version != null ? `v${def.version}` : "v1",
      owner: def.created_by || def.updated_by || "—",
      lastRun: def.last_run_at
        ? new Date(def.last_run_at).toLocaleString()
        : "Not run",
      instances: String(def.instance_count ?? 0),
      failure: `${fail.toFixed(1)}%`,
      failFg: fail > 0 ? "#D92D20" : "var(--s09-text)",
      group: meta.group || "other",
    };
  }

  private statusStyle(st: string): { border: string; fg: string } {
    if (st === "ACTIVE") return { border: "#8FD6B0", fg: "#2E9B5F" };
    if (st === "PAUSED") return { border: "#F0D48A", fg: "#B8860B" };
    if (st === "ARCHIVED") return { border: "#CFD5E0", fg: "#7A8499" };
    return { border: "#C5D4F5", fg: "#1360DF" };
  }
}
